import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output
} from '@angular/core';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { IconDirective } from '@view/shared/atoms/icon/icon.directive';
import { OptionItemComponent } from '@view/shared/molecules/option-item/option-item.component';

/**
 * Componente que representa la barra superior de la aplicación.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-top-bar',
  imports: [IconDirective, OptionItemComponent],
  template: `
    <div class="h-16 w-full bg-white shadow text-center">
      <div class="flex h-full items-center justify-between">
        <div class="flex items-center">
          <div
            tabindex="0"
            class="flex h-16 w-16 cursor-pointer items-center justify-center hover:bg-primary-200"
            (click)="onMenuClicked()"
            (keyup.enter)="onMenuClicked()"
          >
            <i appIcon icon="align-justify" class="text-primary"></i>
          </div>
          <div class="flex items-center gap-3">
            <img src="assets/images/mur-logo.png" alt="logo" class="size-8" />
            <div class="bg-primary-200 h-14 w-1"></div>
            <span class="text-2xl font-bold text-primary ml-2"
              >Mensajería Dinámica - MUR</span
            >
          </div>
        </div>

        <div
          tabindex="1"
          class="flex h-16 cursor-pointer items-center justify-center px-4 hover:bg-blue-200"
          (click)="onUserMenuClicked()"
          (keyup.enter)="onUserMenuClicked()"
        >
          <app-option-item
            [item]="getUserItem()"
            class="text-primary"
          ></app-option-item>
        </div>
      </div>
    </div>
  `,
  styles: ``
})
export class TopBarComponent {
  /** Nombre del usuario */
  username = input.required<string>();

  /** Último acceso del usuario */
  lastAccess = input.required<string>();

  /** Servicio de traducción de la aplicación */
  private translatorService = inject(AppTranslationService);

  /**
   * Emite evento cuando el menú de opciones cambia de estado.
   */
  @Output() menuStateChange = new EventEmitter<string>();

  /**
   * Emite evento cuando el menú de usuario cambia de estado.
   */
  @Output() userMenuStateChange = new EventEmitter<string>();

  /** Estado del menú de opciones y usuario */
  private optionMenuState = 'closed';
  private userMenuState = 'closed';

  /**
   * Construye un item con la información del usuario.
   * @returns item que representa al usuario.
   */
  getUserItem = computed(() => {
    return {
      id: this.username(),
      path: '',
      title: this.username(),
      subtitle: this.getLastConnection(),
      icon: this.getIcon()
    };
  });

  /**
   * Obtiene la última conexión del usuario.
   */
  getLastConnection = computed(
    (): string =>
      `${this.translatorService.get('security.last-access', { lastAccess: this.lastAccess() })}`
  );

  /**
   * Obtiene el icono que se muestra en el menú de usuario.
   */
  getIcon() {
    return this.userMenuState == 'opened' ? 'angle-up' : 'angle-down';
  }

  /**
   * Cambia el estado del menú de opciones.
   */
  onMenuClicked() {
    this.optionMenuState =
      this.optionMenuState === 'closed' ? 'opened' : 'closed';
    this.menuStateChange.emit(this.optionMenuState);
  }

  /**
   * Cambia el estado del menú de usuario.
   */
  onUserMenuClicked() {
    this.userMenuState = this.userMenuState === 'closed' ? 'opened' : 'closed';
    this.userMenuStateChange.emit(this.userMenuState);
  }
}
